import type { UnstyledButtonProps } from '@mantine/core';
import { UnstyledButton, createStyles } from '@mantine/core';
import { rem } from '@mantine/styles';
import { typography } from '@repo/theme/primitives';
import type { ColorNames } from '@repo/theme/utils';
import { forwardRef } from 'react';
import { Icon, type IconNames } from '../Icon';

export type PlainButtonTones = 'default' | 'critical';
export type PlainButtonSizes = 'xs' | 'sm' | 'md';

export interface PlainButtonClassNames {
	root?: string;
	inner?: string;
	label?: string;
	leftIcon?: string;
	rightIcon?: string;
}

interface PlainButtonProps
	extends Omit<
		UnstyledButtonProps,
		'unstyled' | 'variant' | 'children' | 'classNames'
	> {
	/** Controls button appearance  */
	tone?: PlainButtonTones;
	/** Predefined button size */
	size?: PlainButtonSizes;
	/** Button label */
	children: React.ReactNode;
	/** Callback for when it's clicked */
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	/** Disabled state */
	disabled?: boolean;

	/** Adds icon before button label  */
	leftIconName?: IconNames;
	/** Adds icon after button label  */
	rightIconName?: IconNames;

	/** Class names for different parts of the button */
	classNames?: PlainButtonClassNames;
	id?: string;
}

interface PlainButtonStyleProps {
	size: PlainButtonSizes;
	tone: PlainButtonTones;
}

const useStyles = createStyles(
	(theme, { size, tone }: PlainButtonStyleProps) => {
		let labelColor: ColorNames = 'text/emphasis/default';
		let hoverLabelColor: ColorNames = 'text/emphasis/hover';
		let activeLabelColor: ColorNames = 'text/emphasis/active';
		let fontSize: string = typography.text.sm;
		let lineHeight: string = typography.lineHeight.text.sm;

		if (tone === 'critical') {
			labelColor = 'text/critical/default';
			hoverLabelColor = 'text/critical/hover';
			activeLabelColor = 'text/critical/active';
		}

		if (size === 'md') {
			fontSize = typography.text.md;
			lineHeight = typography.lineHeight.text.md;
		} else if (size === 'xs') {
			fontSize = typography.text.xs;
			lineHeight = typography.lineHeight.text.md;
		}

		return {
			container: {
				display: 'inline-block',
				position: 'relative',

				fontSize,
				lineHeight,
				fontWeight: typography.weight.semibold,

				// We need a radius so that the focus state is rounded
				borderRadius: theme.other.space[2],

				color: theme.other.getColor(labelColor),

				textDecorationLine: 'none',

				'&:hover': {
					color: theme.other.getColor(hoverLabelColor),
					textDecorationLine: 'underline',
				},
				'&:active': {
					color: theme.other.getColor(activeLabelColor),
					textDecorationLine: 'underline',
				},
				'&:focus': {
					outline: `solid ${theme.other.getColor(
						'border/emphasis/default'
					)} 2px`,
					outlineOffset: rem(theme.other.space[0.25]),
				},
				'&:disabled': {
					textDecorationLine: 'none',
					color: theme.other.getColor('text/primary/disabled'),
				},
			},
			inner: {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				overflow: 'visible',

				height: '100%',
			},
			label: {
				whiteSpace: 'nowrap',
				height: '100%',
				overflow: 'hidden',
				display: 'flex',
				alignItems: 'center',
			},
		};
	}
);

const PlainButton = forwardRef<HTMLButtonElement, PlainButtonProps>(
	(
		{
			id,
			children,
			onClick,
			tone = 'default',
			size = 'sm',
			disabled = false,
			leftIconName,
			rightIconName,
			className,
			classNames,
			styles,
			...other
		},
		ref
	) => {
		const { classes, cx } = useStyles({ size, tone });

		return (
			<UnstyledButton
				id={id}
				className={cx(classes.container, className, classNames?.root)}
				styles={styles}
				disabled={disabled}
				onClick={onClick}
				ref={ref}
				{...other}
			>
				<span className={cx(classes.inner, classNames?.inner)}>
					{leftIconName && (
						<Icon name={leftIconName} className={classNames?.leftIcon} />
					)}
					<span className={cx(classes.label, classNames?.label)}>
						{children}
					</span>
					{rightIconName && (
						<Icon name={rightIconName} className={classNames?.rightIcon} />
					)}
				</span>
			</UnstyledButton>
		);
	}
);

PlainButton.displayName = 'PlainButton';

export default PlainButton;
